/**
 * Notification Service
 *
 * Displays notifications to users (proactive messages, errors, etc.)
 * Can be extended to show toast notifications, modal messages, etc.
 */

import { bus } from '../../core/events.js';

export interface Notification {
  id?: string;
  serverNotificationId?: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'proactive';
  duration?: number; // Auto-dismiss after this many ms (0 = don't auto-dismiss)
  timestamp: number;
}

type NotificationListener = (notification: Notification) => void;

const listeners: Set<NotificationListener> = new Set();

/**
 * Show a notification
 */
export function showNotification(notification: Omit<Notification, 'timestamp'>): void {
  const fullNotification: Notification = {
    ...notification,
    id: notification.id ?? (typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : `notification-${Date.now()}-${Math.random().toString(36).slice(2)}`),
    timestamp: Date.now(),
  };
  
  // Emit to all listeners
  listeners.forEach(listener => listener(fullNotification));
  const mappedType = notification.type === 'proactive' ? 'info' : notification.type;
  bus.emit('notification.show', {
    type: mappedType,
    message: notification.message,
    duration: notification.duration,
    serverNotificationId: notification.serverNotificationId,
  });
  
  // Also log to console for now (can be replaced with UI later)
  const emoji = {
    info: 'ℹ️',
    success: '✅',
    warning: '⚠️',
    error: '❌',
    proactive: '💬',
  }[notification.type];
  
  console.log(`${emoji} [${notification.type.toUpperCase()}] ${notification.message}`);
}

/**
 * Subscribe to notifications
 */
export function onNotification(listener: NotificationListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Show proactive message as notification
 */
export function showProactiveNotification(message: string): void {
  showNotification({
    message,
    type: 'proactive',
    duration: 0, // Don't auto-dismiss proactive messages
  });
}

